// src/contexts/ContentContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import { db } from "../firebase/firebase";
import { doc, onSnapshot, setDoc } from "firebase/firestore";

const ContentContext = createContext(null);

export const DEFAULT_CONTENT_INFO = {
  logoURL: "https://i.postimg.cc/jS7XfQWz/main-logo.png",
  footerLogoURL: "https://i.postimg.cc/xCyD2xW6/main-footer-logo.png",
  bankInfo: {
    bankName: "",
    accountHolder: "",
    accountNumber: "",
    whatsappNumber: "",
  },
  slides: [
    {
      heroTagline: "HealthCare sa mily Gi",
      heroTitle: "100% Genuine Dwaai",
      heroTitleHighlight: "Ab Gar Bethey!",
      heroSubtitle: "Order Your Medicines Now!",
      heroCTAButtonText: "10% OFF",
      heroImageURL: "https://res.cloudinary.com/dzng4fuzh/image/upload/v1776163098/thy1gq688b627ek7bcvy.png",
    },
    {
      heroTagline: "Asal Dawaai, Behtareen Sehat",
      heroTitle: "Har Medicine Authentic & Trusted",
      heroTitleHighlight: "Ghar Bethay Lejayein",
      heroSubtitle: "Online Order Krein Abhi",
      heroCTAButtonText: "10% OFF",
      heroImageURL: "https://res.cloudinary.com/dzng4fuzh/image/upload/v1776163442/teabhztmodqn2zedadbk.png",
    },
    {
      heroTagline: "Behtreen Sehat Ka Waada",
      heroTitle: "Har Dawaai 100% Authentic,",
      heroTitleHighlight: "Our Priority!",
      heroSubtitle: "Ghar Tak Tez Delivery",
      heroCTAButtonText: "10% OFF",
      heroImageURL: "https://res.cloudinary.com/dynqsbpgw/image/upload/v1776679240/og8xegy4m7k0tuwctx07.png",
    },
  ],
  contact: {
    phoneNumber: "+053-3607674",
    whatsappNumber: "0320-7561425",
    email: "info@dawaacare.com",
    location: "Find us right opposite Ikram Hospital, Bhimber road Gujrat.",
  },
  responsibilitiesSection: {
    items: [
      { title: "Dispensing Medications", desc: "Ensuring the safe and accurate distribution of prescribed medicines to patients while providing proper dosage instructions" },
      { title: "Patient Counseling", desc: "Educating customers about their medications, potential side effects, and interactions to promote safe and effective use." },
      { title: "Health & Wellness Advice", desc: "Offering guidance on managing chronic conditions, lifestyle improvements, and over-the-counter medication recommendations." },
      { title: "Medication Management", desc: "Monitoring prescriptions to prevent harmful drug interactions, ensuring compliance, and assisting with medication refills" },
      { title: "Over-the-Counter Assistance", desc: "Helping customers choose the right non-prescription medications for common ailments and providing usage guidance." },
      { title: "Immunization Services", desc: "Administering vaccines, offering immunization advice, and ensuring compliance with health regulations for disease prevention." },
    ],
    bannerHeading: "Improving Health With Best Medicines",
    bannerDescription: "Elevate your health with our expert advice and personalized care. We're here to support you on your health journey.",
    bannerButtonText: "Contact Us",
    bannerButtonLink: "/",
    bannerImageURL: "https://i.postimg.cc/c422tG7j/pharmacist.png",
  },
  ads: {
    ad1URL: "https://res.cloudinary.com/dlzibgsjw/image/upload/v1777964080/lrhwtfltwkwv18wzjaha.png",
    ad2URL: "https://res.cloudinary.com/dlzibgsjw/image/upload/v1777964457/sf2vnalyvdnvssbpnfas.png",
  },
};

// Migrate a single slide from old field names to new field names
function migrateSlide(slide) {
  return {
    heroTagline: slide.heroTagline ?? slide.tagline ?? "",
    heroTitle: slide.heroTitle ?? slide.title ?? "",
    heroTitleHighlight: slide.heroTitleHighlight ?? slide.bigSpan ?? "",
    heroSubtitle: slide.heroSubtitle ?? slide.subtitle ?? "",
    heroCTAButtonText: slide.heroCTAButtonText ?? slide.buttonText ?? "",
    heroImageURL: slide.heroImageURL ?? slide.imageURL ?? "",
  };
}

export function ContentProvider({ children }) {
  const [contentInfo, setContentInfo] = useState(DEFAULT_CONTENT_INFO);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ref = doc(db, "siteSettings", "ContentManagement");
    const unsubscribe = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setContentInfo({
          logoURL: data.logoURL || DEFAULT_CONTENT_INFO.logoURL,
          footerLogoURL: data.footerLogoURL || DEFAULT_CONTENT_INFO.footerLogoURL,
          slides: data.slides ? data.slides.map(migrateSlide) : DEFAULT_CONTENT_INFO.slides,
          contact: data.contact
            ? {
              ...DEFAULT_CONTENT_INFO.contact,
              ...data.contact,
              // migrate old field names from existing Firestore documents
              phoneNumber: data.contact.phoneNumber ?? data.contact.phone1 ?? DEFAULT_CONTENT_INFO.contact.phoneNumber,
              whatsappNumber: data.contact.whatsappNumber ?? data.contact.phone2 ?? DEFAULT_CONTENT_INFO.contact.whatsappNumber,
            }
            : DEFAULT_CONTENT_INFO.contact,
          bankInfo: data.bankInfo
            ? { ...DEFAULT_CONTENT_INFO.bankInfo, ...data.bankInfo }
            : DEFAULT_CONTENT_INFO.bankInfo,
          responsibilitiesSection: data.responsibilitiesSection
            ? {
              ...DEFAULT_CONTENT_INFO.responsibilitiesSection,
              ...data.responsibilitiesSection,
              items: data.responsibilitiesSection.items?.length
                ? data.responsibilitiesSection.items
                : DEFAULT_CONTENT_INFO.responsibilitiesSection.items,
            }
            : DEFAULT_CONTENT_INFO.responsibilitiesSection,
          ads: data.ads
            ? { ...DEFAULT_CONTENT_INFO.ads, ...data.ads }
            : DEFAULT_CONTENT_INFO.ads,
        });
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const saveContentInfo = async (newInfo) => {
    const ref = doc(db, "siteSettings", "ContentManagement");
    await setDoc(ref, newInfo, { merge: true });
  };

  return (
    <ContentContext.Provider value={{ contentInfo, loading, saveContentInfo }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContentInfo() {
  return useContext(ContentContext);
}
